import { TagTone } from '../components/ui';
import { ListWatchlistResponse, NcAuctionBidConfig, NcAuctionBidMode, NcAuctionWatchlistItem, WatchlistBidSettings } from '../api/models';

export const DEFAULT_BID_MODE = 'closeout';

export interface BidModeOption {
    value: NcAuctionBidMode;
    label: string;
    description: string;
}

// The descriptions spell out which gates each mode drops, because the names on
// their own do not say what the bidder will actually do.
export const BID_MODE_OPTIONS: BidModeOption[] = [
    {
        value: 'closeout',
        label: 'Closeout sniper',
        description: 'Only bids on an auction that ended with no takers and dropped into the 24-hour closeout, and only inside the bid window. Skips anything priced above the reject threshold.',
    },
    {
        value: 'snipe',
        label: 'Snipe',
        description: 'Bids inside the bid window regardless of price or competition. Does not wait for a closeout.',
    },
    {
        value: 'on_sight',
        label: 'On sight',
        description: 'Bids as soon as a live auction is found, with no price or timing gate. Namecheap then runs the proxy bid up to your maximum.',
    },
];

export function bidModeLabel(mode: string): string {
    return BID_MODE_OPTIONS.find((o) => o.value === mode)?.label ?? mode;
}

export function bidModeDescription(mode: string): string {
    return BID_MODE_OPTIONS.find((o) => o.value === mode)?.description ?? '';
}

export function isTargetedMode(mode: string): boolean {
    return mode === 'snipe' || mode === 'on_sight';
}

// On sight is the loudest because it is the only mode that will spend money
// with no timing gate at all.
export function bidModeTone(mode: string): TagTone {
    switch (mode) {
        case 'on_sight':
            return 'danger';
        case 'snipe':
            return 'warn';
        default:
            return 'neutral';
    }
}

export interface EffectiveValue {
    value: number;
    inherited: boolean;
}

export function effectiveMaxBid(item: NcAuctionWatchlistItem, config?: NcAuctionBidConfig | null): EffectiveValue | null {
    if (item.maxBidAmount !== null && item.maxBidAmount !== undefined) {
        return { value: item.maxBidAmount, inherited: false };
    }
    if (!config) return null;
    return { value: config.maxBidAmount, inherited: true };
}

export function effectiveThreshold(item: NcAuctionWatchlistItem, config?: NcAuctionBidConfig | null): EffectiveValue | null {
    if (item.minBidRejectThreshold !== null && item.minBidRejectThreshold !== undefined) {
        return { value: item.minBidRejectThreshold, inherited: false };
    }
    if (!config) return null;
    return { value: config.minBidRejectThreshold, inherited: true };
}

export const MAX_ALLOWED_BID_AMOUNT = 100000;
export const MAX_ALLOWED_BID_WINDOW_MINUTES = 720;

// On sight has no timing band at all, so the window and floor are inert there.
export function usesTimingBand(mode: string): boolean {
    return mode !== 'on_sight';
}

export function effectiveWindowMinutes(item: NcAuctionWatchlistItem, config?: NcAuctionBidConfig | null): EffectiveValue | null {
    if (item.bidWindowMinutes !== null && item.bidWindowMinutes !== undefined) {
        return { value: item.bidWindowMinutes, inherited: false };
    }
    if (!config) return null;
    return { value: config.bidWindowMinutes, inherited: true };
}

export function effectiveFloorMinutes(item: NcAuctionWatchlistItem, config?: NcAuctionBidConfig | null): EffectiveValue | null {
    if (item.bidFloorMinutes !== null && item.bidFloorMinutes !== undefined) {
        return { value: item.bidFloorMinutes, inherited: false };
    }
    if (!config) return null;
    return { value: config.bidFloorMinutes, inherited: true };
}

export interface BidSettingsFormValues {
    domain?: string;
    bidMode?: string;
    maxBidAmount?: number | null;
    minBidRejectThreshold?: number | null;
    bidWindowMinutes?: number | null;
    bidFloorMinutes?: number | null;
}

// Mirrors the server rules so the form can fail without a round trip. The
// server stays authoritative.
export function validateBidSettings(
    values: BidSettingsFormValues,
    config?: NcAuctionBidConfig | null,
    options: { requireDomain?: boolean } = {},
): Record<string, string> {
    const errors: Record<string, string> = {};

    if (options.requireDomain) {
        const domain = (values.domain ?? '').trim();
        if (!domain) {
            errors.domain = 'Domain is required';
        } else if (!domain.includes('.') || domain.endsWith('.')) {
            errors.domain = 'Domain must include a TLD';
        }
    }

    const mode = values.bidMode ?? DEFAULT_BID_MODE;
    if (!BID_MODE_OPTIONS.some((o) => o.value === mode)) {
        errors.bidMode = 'Pick one of the listed bid modes';
    }

    const max = values.maxBidAmount;
    if (max !== null && max !== undefined) {
        if (!Number.isFinite(max) || max <= 0) {
            errors.maxBidAmount = 'Max bid must be greater than zero';
        } else if (max > MAX_ALLOWED_BID_AMOUNT) {
            errors.maxBidAmount = `Max bid must not exceed ${MAX_ALLOWED_BID_AMOUNT}`;
        }
    }

    const threshold = values.minBidRejectThreshold;
    if (threshold !== null && threshold !== undefined) {
        if (!Number.isFinite(threshold) || threshold < 0) {
            errors.minBidRejectThreshold = 'Reject threshold must not be negative';
        }
    }

    if (isTargetedMode(mode) && (max === null || max === undefined)) {
        errors.maxBidAmount = config
            ? `A ${bidModeLabel(mode).toLowerCase()} row needs its own max bid: it would otherwise inherit the global $${config.maxBidAmount}`
            : 'A targeted row needs its own max bid';
    }

    const window = values.bidWindowMinutes;
    if (window !== null && window !== undefined) {
        if (!Number.isFinite(window) || window <= 0) {
            errors.bidWindowMinutes = 'Bid window must be greater than zero';
        } else if (window > MAX_ALLOWED_BID_WINDOW_MINUTES) {
            errors.bidWindowMinutes = `Bid window must not exceed ${MAX_ALLOWED_BID_WINDOW_MINUTES} minutes`;
        }
    }

    const floor = values.bidFloorMinutes;
    if (floor !== null && floor !== undefined) {
        if (!Number.isFinite(floor) || floor < 0) {
            errors.bidFloorMinutes = 'Bid floor must not be negative';
        }
    }

    if (!errors.bidWindowMinutes && !errors.bidFloorMinutes && config && usesTimingBand(mode)) {
        const resolvedWindow = window ?? config.bidWindowMinutes;
        const resolvedFloor = floor ?? config.bidFloorMinutes;
        if (resolvedWindow < resolvedFloor) {
            errors.bidWindowMinutes = `Bid window ${resolvedWindow} min is below the floor ${resolvedFloor} min, so the row would never bid`;
        }
    }

    if (!errors.maxBidAmount && !errors.minBidRejectThreshold && config) {
        const resolvedMax = max ?? config.maxBidAmount;
        const resolvedThreshold = threshold ?? config.minBidRejectThreshold;
        if (resolvedMax < resolvedThreshold) {
            errors.maxBidAmount = `Max bid $${resolvedMax} is below the reject threshold $${resolvedThreshold}`;
        }
    }

    return errors;
}

// Blank means "inherit", which has to reach the server as an explicit null so
// it clears the stored override instead of being ignored.
export function toBidSettingsRequest(values: BidSettingsFormValues): WatchlistBidSettings {
    return {
        bidMode: values.bidMode as NcAuctionBidMode | undefined,
        maxBidAmount: values.maxBidAmount ?? null,
        minBidRejectThreshold: values.minBidRejectThreshold ?? null,
        bidWindowMinutes: values.bidWindowMinutes ?? null,
        bidFloorMinutes: values.bidFloorMinutes ?? null,
    };
}

// Replaces a row in place so an edit does not reset the operator's position in
// a list they are working through.
export function upsertWatchlistRow(
    list: ListWatchlistResponse | null,
    row: NcAuctionWatchlistItem,
): ListWatchlistResponse | null {
    if (!list) return list;
    const index = list.content.findIndex((item) => item.id === row.id);
    if (index < 0) return list;
    const content = [...list.content];
    content[index] = row;
    return { ...list, content };
}

export function statusIsRevivable(status: string): boolean {
    return ['rejected', 'closeout_missed', 'lost', 'removed'].includes(status);
}

export function statusIsCommitted(status: string): boolean {
    return status === 'bid_placed' || status === 'won';
}
